import {AbstractControl, FormArray, ValidationErrors, ValidatorFn} from '@angular/forms';
import { Const } from './Const';
import { CostObjectModel } from '../models/misc/costObject.model';

export class CustomValidators {
    static ValidateTechnicalName(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.TECHNICAL_NAME_EXP)) {
            return { invalidName: true };
        }
        return null;
    }

    static ValidateDisplayName(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.DISPLAY_NAME_EXP)) {
            return { invalidDisplayName: true };
        }

        return null;
    }

    public static ValidateStartsWith(val: string, message?: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const value = control.value;

            if (value == null || value === '') {
                return null;
            }
            if (!value?.startsWith(val)) {
                return { invalidStartsWith: {res: false, val: val, message: message} };
            }
            return null;
        };
    }

    public static ValidateEndsWith(val: string, message?: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const value = control.value;

            if (value == null || value === '') {
                return null;
            }
            if (!value?.endsWith(val)) {
                return { invalidEndsWith: { res: false, val, message } };
            }
            return null;
        };
    }

    public static ValidatePattern(regex: RegExp, message?: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const value = control.value;

            if (value == null || value === '') {
                return null;
            }

            if (!regex.test(value)) {
                return { pattern: {res: false, message: message, requiredPattern: regex.toString()} };
            }
            return null;
        };
    }

    static ValidateDescription(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.DESCRIPTION_EXP)) {
            return { invalidDescription: true };
        }

        return null;
    }

    static ValidatePath(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.PATH_EXP)) {
            return { invalidPath: true };
        }
        return null;
    }

    static ValidateUrmGroupName(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.URM_GROUP_NAME_EXP)) {
            return { invalidUrmGroupName: true };
        }
        return null;
    }

    static ValidateIconUrl(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.ICON_URL_EXP)) {
            return { invalidIconUrl: true };
        }
        return null;
    }

    static ValidateHttpUrl(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.HTTP_URL_EXP)) {
            return { invalidHttpUrl: true };
        }
        return null;
    }

    static ValidateTenantProductType(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.TPT_EXP)) {
            return { invalidTpt: true };
        }
        return null;
    }

    static ValidateVersion(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.VERSION_EXP)) {
            return { invalidVersion: true };
        }
        return null;
    }

    static ValidatePascalCase(control: AbstractControl): { [key: string]: any } | null {
        if (control.value && !control.value.match(Const.TYPE_EXP)) {
            return { invalidType: true };
        }
        return null;
    }

    public static ValidateCostObject(costObjectList: CostObjectModel[]): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            if (!control.value) {
                return null;
            }
            let exists = costObjectList.some((item) => item.COST_OBJECT_ID === control.value);
            if (!exists) {
                return { invalidCostObject: true };
            }
            return null;
        };
    }


    public static ValidateDisallowValues(disallowed: string[], message?: string, isCaseSensitive = false): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const controlValue = control.value;
            const match = disallowed.some(item => {
                if (isCaseSensitive) {
                    return item === controlValue;
                } else {
                    return item?.toLowerCase() === controlValue?.toLowerCase();
                }
            });

            return match
                ? { disallowedValue: { value: controlValue, message: message } }
                : null;
        };
    }

    public static ValidateDuplicatePropertyValidator(formArray: FormArray, prop: string, message?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!formArray || !control) {
                return null;
            }

            const currentValue = control.value;
            const isDuplicate = formArray.controls
                .filter(group => {
                    const targetControl = group.get(prop);
                    return targetControl && targetControl !== control && targetControl?.value?.toLowerCase() === currentValue?.toLowerCase();
                })
                .length > 0;

            return isDuplicate
                ? { duplicate: { value: currentValue, message } }
                : null;
        };
    }

    /**
     * Reject a name that already belongs to another item in `getCollection()`.
     *
     * The collection and editing-index are read via callbacks so the
     * validator always resolves against the **current** instance state at
     * lookup time — values captured by a closure at validator-creation time
     * would go stale as state-service subscriptions hydrate the lists.
     *
     * Emits `{ duplicateName: true }` on collision; pair it with
     * `VALIDATION_MESSAGES['duplicateName']` for the user-facing copy. Empty
     * values are deferred to `Validators.required`.
     *
     * @param getCollection      Returns the collection to search.
     * @param getEditingIndex    Returns the index being edited, or `null`
     *                           for an "add" flow. The matching index is
     *                           skipped so the user can save edits without
     *                           renaming.
     * @param getName            Pulls the name out of a collection item.
     * @param extraFilter        Optional predicate that further narrows the
     *                           collision check (e.g., principal-name
     *                           uniqueness scoped to a specific type).
     *                           Items where this returns `false` are NOT
     *                           treated as collisions.
     */
    public static UniqueName<T>(
        getCollection: () => T[] | null | undefined,
        getEditingIndex: () => number | null,
        getName: (item: T) => string,
        extraFilter?: (item: T) => boolean,
        normalizeValue?: (v: string) => string
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const raw = (control.value || '').trim();
            if (!raw) return null;
            const value = normalizeValue ? normalizeValue(raw) : raw;
            const collection = getCollection();
            if (!collection || collection.length === 0) return null;
            const editingIndex = getEditingIndex();
            const collision = collection.some((item, i) => {
                if (i === editingIndex) return false;
                if (getName(item) !== value) return false;
                return extraFilter ? extraFilter(item) : true;
            });
            return collision ? { duplicateName: true } : null;
        };
    }
}
